/**
 * Shared filmmaker search hook - works on both web and mobile
 *
 * Uses dependency injection for the Supabase client to remain platform-agnostic.
 * Each platform (web/mobile) wraps this with their own supabase instance.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  SearchFilmmakerResult,
  SearchFilters,
  SearchAnalyticsData,
  SearchHookOptions,
  SearchUserResult,
  UserSearchFilters,
  AccountTypeFilter,
} from '../types/search';

// Re-export types for convenience
export type {
  SearchFilmmakerResult,
  SearchFilters,
  SearchAnalyticsData,
  SearchHookOptions,
  SearchUserResult,
  UserSearchFilters,
  AccountType,
  AccountTypeFilter,
} from '../types/search';

// ============================================================================
// API FUNCTIONS (take supabase client as parameter)
// ============================================================================

export async function searchFilmmakers(
  supabase: SupabaseClient,
  filters: SearchFilters
): Promise<SearchFilmmakerResult[]> {
  const { data, error } = await supabase.rpc('search_filmmakers', {
    params: filters
  });

  if (error) {
    console.error('[search_filmmakers] Error:', error);
    throw error;
  }

  return data || [];
}

export async function logSearchAnalytics(
  supabase: SupabaseClient,
  analytics: SearchAnalyticsData
): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('search_analytics')
    .insert({
      user_id: user?.id,
      session_id: analytics.session_id,
      query_text: analytics.query_text,
      filters_used: analytics.filters_used,
      ranking_variant: analytics.ranking_variant,
      results_count: analytics.results_count,
      results_returned: analytics.results_returned,
      response_time_ms: analytics.response_time_ms,
      search_context: analytics.search_context,
      platform: analytics.platform,
    })
    .select('id')
    .single();

  if (error) {
    console.warn('[logSearchAnalytics] Failed to log:', error);
    return null;
  }

  return data?.id || null;
}

export async function logSearchClick(
  supabase: SupabaseClient,
  searchId: string,
  clickedFilmmakerId: string,
  clickedPosition: number,
  timeToClickMs: number
): Promise<void> {
  const { error } = await supabase.rpc('log_search_click', {
    p_search_id: searchId,
    p_clicked_filmmaker_id: clickedFilmmakerId,
    p_clicked_position: clickedPosition,
    p_time_to_click_ms: timeToClickMs
  });

  if (error) {
    console.warn('[logSearchClick] Failed to log click:', error);
  }
}

/**
 * Search ALL users (filmmakers, clients, or both account types)
 * Use this for chat, groups, and general user lookups
 */
export async function searchUsers(
  supabase: SupabaseClient,
  filters: UserSearchFilters
): Promise<SearchUserResult[]> {
  const { data, error } = await supabase.rpc('search_users', {
    params: filters
  });

  if (error) {
    console.error('[search_users] Error:', error);
    throw error;
  }

  return data || [];
}

// ============================================================================
// HOOK FACTORY
// Creates hooks bound to a specific Supabase client
// ============================================================================

export function createSearchHooks(supabase: SupabaseClient) {
  /**
   * Universal filmmaker search hook
   *
   * @example
   * // Full search with filters
   * const { data } = useSearchFilmmakers({
   *   query: 'wedding videographer',
   *   location: 'London',
   *   min_rating: 4.0,
   *   has_portfolio: true
   * });
   *
   * @example
   * // Featured talent (top rated)
   * const { data } = useSearchFilmmakers({
   *   is_verified: true,
   *   min_rating: 4.5,
   *   sort_by: 'rating',
   *   limit: 6
   * });
   *
   * @example
   * // Quick user lookup for chat
   * const { data } = useSearchFilmmakers({
   *   query: 'john',
   *   limit: 10
   * });
   */
  function useSearchFilmmakers(
    filters: SearchFilters,
    options?: SearchHookOptions
  ) {
    return useQuery({
      queryKey: ['filmmakers', 'search', filters],
      queryFn: () => searchFilmmakers(supabase, filters),
      enabled: options?.enabled ?? true,
      staleTime: options?.staleTime ?? 30_000, // 30 seconds default
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    });
  }

  /**
   * Hook for logging search analytics
   */
  function useSearchAnalytics(context: string, platform: 'web' | 'ios' | 'android') {
    const logMutation = useMutation({
      mutationFn: (data: Omit<SearchAnalyticsData, 'search_context' | 'platform'>) =>
        logSearchAnalytics(supabase, {
          ...data,
          search_context: context,
          platform,
        }),
    });

    const clickMutation = useMutation({
      mutationFn: (data: {
        searchId: string;
        filmmakerIdClicked: string;
        position: number;
        timeToClickMs: number;
      }) => logSearchClick(
        supabase,
        data.searchId,
        data.filmmakerIdClicked,
        data.position,
        data.timeToClickMs
      ),
    });

    return {
      logSearch: logMutation.mutateAsync,
      logClick: clickMutation.mutate,
      isLogging: logMutation.isPending,
    };
  }

  // ============================================================================
  // CONVENIENCE HOOKS
  // ============================================================================

  /**
   * Hook for featured filmmakers
   * Requires profile picture, portfolio, and verification
   * Sorted by rating (highest first)
   */
  function useFeaturedFilmmakers(limit = 6) {
    return useSearchFilmmakers({
      is_verified: true,
      has_portfolio: true,
      has_profile_pic: true,
      sort_by: 'rating',
      limit,
    });
  }

  /**
   * Hook for searching filmmakers by name (for chat/group lookups)
   */
  function useFilmmakerNameSearch(query: string, limit = 10) {
    return useSearchFilmmakers(
      { query, limit },
      { enabled: query.length >= 2 }
    );
  }

  /**
   * Hook for fetching specific filmmakers by ID
   */
  function useFilmmakersByIds(filmmakerIds: string[]) {
    return useSearchFilmmakers(
      { filmmaker_ids: filmmakerIds, limit: filmmakerIds.length },
      { enabled: filmmakerIds.length > 0 }
    );
  }

  /**
   * Hook for fetching filmmakers by user IDs
   */
  function useFilmmakersByUserIds(userIds: string[]) {
    return useSearchFilmmakers(
      { user_ids: userIds, limit: userIds.length },
      { enabled: userIds.length > 0 }
    );
  }

  // ============================================================================
  // USER SEARCH HOOKS (all account types)
  // ============================================================================

  /**
   * Universal user search hook - searches filmmakers, clients, or both
   *
   * @example
   * // Search all users for chat
   * const { data } = useSearchUsers({ query: 'john', limit: 10 });
   *
   * @example
   * // Search only clients
   * const { data } = useSearchUsers({ account_type: 'client', limit: 20 });
   *
   * @example
   * // Search dual accounts only
   * const { data } = useSearchUsers({ account_type: 'both', limit: 20 });
   *
   * @example
   * // Search anyone with a filmmaker account (includes 'both')
   * const { data } = useSearchUsers({ account_type: 'has_filmmaker' });
   */
  function useSearchUsers(
    filters: UserSearchFilters,
    options?: SearchHookOptions
  ) {
    return useQuery({
      queryKey: ['users', 'search', filters],
      queryFn: () => searchUsers(supabase, filters),
      enabled: options?.enabled ?? true,
      staleTime: options?.staleTime ?? 30_000,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    });
  }

  /**
   * Search users by name - for chat/group member selection
   * Searches ALL account types by default
   */
  function useUserNameSearch(query: string, accountType?: AccountTypeFilter, limit = 10) {
    return useSearchUsers(
      { query, account_type: accountType, limit },
      { enabled: query.length >= 2 }
    );
  }

  /**
   * Fetch specific users by ID
   */
  function useUsersByIds(userIds: string[]) {
    return useSearchUsers(
      { user_ids: userIds, limit: userIds.length },
      { enabled: userIds.length > 0 }
    );
  }

  return {
    // Filmmaker-specific search
    useSearchFilmmakers,
    useFeaturedFilmmakers,
    useFilmmakerNameSearch,
    useFilmmakersByIds,
    useFilmmakersByUserIds,
    // Universal user search (all account types)
    useSearchUsers,
    useUserNameSearch,
    useUsersByIds,
    // Analytics
    useSearchAnalytics,
  };
}
